// HTTP client (architecture §21.4, WP0). JSON in and out, cookies always, German ApiError on failure.
// 401 → global handler (clears the cache and navigates to /login). 423 `budget` → toast "Trotzdem senden" that resends
// the same request with `budgetOverride: true`. 403 `stepup_noetig` → callers wrap the call in `withStepUp()` (webauthn.ts).
import { ApiError, isApiErrorBody } from '@hq/shared';
import type { QueryClient } from '@tanstack/react-query';
import { toast, useUiStore } from '../state/ui.ts';
import { disconnectEvents } from './sse.ts';

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiOptions {
  signal?: AbortSignal;
  /** Called with the result when Chris tapped "Trotzdem senden" after a 423 budget stop. */
  onBudgetResent?: (result: unknown) => void;
  /** Skips the budget toast (the caller handles 423 itself). */
  noBudgetToast?: boolean;
  keepalive?: boolean;
  headers?: Record<string, string>;
}

let unauthorizedHandler: (() => void) | null = null;
/** main.tsx installs this: clear the Query cache and navigate to /login. */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

export const OFFLINE_MESSAGE = 'Keine Verbindung. Bitte prüf dein Netz und versuch es nochmal.';

function isBodyInit(b: unknown): b is FormData | Blob | ArrayBuffer {
  return (typeof FormData !== 'undefined' && b instanceof FormData) || (typeof Blob !== 'undefined' && b instanceof Blob) || b instanceof ArrayBuffer;
}

/** The one route whose body takes `budgetOverride` (the 423 toast's "Trotzdem senden"). */
const MESSAGES_ROUTE = /^\/api\/threads\/[^/?#]+\/messages$/;

/** Calls `/api/...`. `path` starts with `/api/`. Returns `undefined` for 204. */
export async function api<T>(method: Method, path: string, body?: unknown, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (isBodyInit(body)) payload = body;
    else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }
  let res: Response;
  try {
    res = await fetch(path, {
      method, headers, body: payload, credentials: 'include', signal: opts.signal, keepalive: opts.keepalive,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (res.ok) return data as T;

  const err = isApiErrorBody(data)
    ? new ApiError(res.status, data.error.code, data.error.message, data.error.details)
    : new ApiError(res.status, res.status >= 500 ? 'intern' : 'ungueltig', res.status >= 500 ? 'Der Server hat gerade ein Problem. Bitte gleich nochmal.' : `Anfrage fehlgeschlagen (${res.status}).`);

  if (res.status === 401 && !path.startsWith('/api/auth/')) unauthorizedHandler?.();
  if (res.status === 423 && err.code === 'budget' && !opts.noBudgetToast) {
    // Only the messages route accepts budgetOverride; the strict bodies of other routes reject it (HANDOFF-WP4 §6.8.1)
    const canResend = method === 'POST' && MESSAGES_ROUTE.test(path) && body !== undefined && !isBodyInit(body) && typeof body === 'object' && body !== null;
    toast({
      tone: 'urgent',
      text: err.message || 'Das Monatsbudget ist erreicht.',
      ...(canResend ? {
        action: {
          label: 'Trotzdem senden',
          run: () => {
            api<unknown>(method, path, { ...(body as Record<string, unknown>), budgetOverride: true }, { ...opts, noBudgetToast: true })
              .then((r) => opts.onBudgetResent?.(r))
              .catch((e: unknown) => toast({ tone: 'urgent', text: e instanceof ApiError ? e.message : 'Senden fehlgeschlagen.' }));
          },
        },
      } : {}),
    });
  }
  throw err;
}

export const get = <T>(path: string, opts?: ApiOptions) => api<T>('GET', path, undefined, opts);
export const post = <T>(path: string, body?: unknown, opts?: ApiOptions) => api<T>('POST', path, body ?? {}, opts);
export const patch = <T>(path: string, body: unknown, opts?: ApiOptions) => api<T>('PATCH', path, body, opts);
export const put = <T>(path: string, body: unknown, opts?: ApiOptions) => api<T>('PUT', path, body, opts);
export const del = <T = void>(path: string, opts?: ApiOptions) => api<T>('DELETE', path, undefined, opts);

/** German message for any thrown value (for toasts and inline errors). */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.name === 'NotAllowedError') return 'Abgebrochen.';
  return 'Etwas ist schiefgelaufen.';
}

export { ApiError };

/**
 * Logout (§8.4): after the 204 it closes the EventSource, deletes every Cache Storage cache, removes the composer drafts
 * (`hq:draft:*`) and clears the query cache, then navigates to /login. The local cleanup runs even if the request fails.
 */
export async function logout(qc: QueryClient, go: (path: string) => void): Promise<void> {
  try {
    await post('/api/auth/logout');
  } catch {
    /* offline or already logged out: the local cleanup still runs */
  } finally {
    disconnectEvents();
    try {
      if (typeof caches !== 'undefined') {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch {
      /* Cache Storage unavailable (private mode) */
    }
    useUiStore.getState().clearAllDrafts();
    qc.clear();
    go('/login');
  }
}
